import { Component, inject } from '@angular/core';
import { ListService } from '@abp/ng.core';
import {
  CardBodyComponent,
  CardComponent,
  ConfirmationService,
} from '@abp/ng.theme.shared';
import {
  BaseCrudComponent,
  CrudPage,
  GenericForm,
  ICrudService,
} from '@zendra/shared';
import { ForestryBaseService } from '../proxy/geographic/controllers';
import {
  ForestryBaseCreateDto,
  ForestryBaseDto,
  ForestryBaseExcelDownloadDto,
  GetForestryBasesInputDto,
  ForestryBaseUpdateDto,
} from '../proxy/geographic/dtos/forestry-base';
import { FORESTRYBASE_CONFIG, FORESTRYBASE_FIELDS } from './forestry-base.config';

type ForestryBaseGridQuery = Omit<GetForestryBasesInputDto, 'regionCode' | 'propertyCode'> & {
  regionCode?: string | string[] | null;
  propertyCode?: string | string[] | null;
};

@Component({
  selector: 'app-forestry-base',
  imports: [CardComponent, CardBodyComponent, CrudPage, GenericForm],
  providers: [ListService, ForestryBaseService, ConfirmationService],
  templateUrl: './forestry-base.html',
  styleUrl: './forestry-base.scss',
})
export class ForestryBase extends BaseCrudComponent<
  ForestryBaseDto,
  ForestryBaseCreateDto,
  ForestryBaseUpdateDto,
  ForestryBaseExcelDownloadDto
> {
  geographicService = inject(ForestryBaseService);
  config = FORESTRYBASE_CONFIG;
  fields = FORESTRYBASE_FIELDS;

  service: ICrudService<
    ForestryBaseDto,
    ForestryBaseCreateDto,
    ForestryBaseUpdateDto,
    ForestryBaseExcelDownloadDto
  > = {
    create: input => this.geographicService.create(input),
    update: (id, input) => this.geographicService.update(id, input),
    deleteByIds: ids => this.geographicService.deleteByIds(ids),
    getList: (query: GetForestryBasesInputDto) =>
      this.geographicService.getList(this.sourceQuery(query)),
    getDownloadToken: () => this.geographicService.getDownloadToken(),
    getListAsExcelFile: input =>
      this.geographicService.getListAsExcelFile(this.sourceQuery(input)),
  };

  private sourceQuery<T extends ForestryBaseGridQuery>(input: T) {
    const query = { ...input };
    if (typeof input.regionCode === 'string') {
      query.regionCode = undefined;
      query.regionCodeContains = input.regionCode;
    }
    if (typeof input.propertyCode === 'string') {
      query.propertyCode = undefined;
      query.propertyCodeContains = input.propertyCode;
    }
    return query as Omit<T, 'regionCode' | 'propertyCode'> & GetForestryBasesInputDto;
  }

  protected prepareUpdateDto(
    data: any,
    selected: ForestryBaseDto
  ): ForestryBaseUpdateDto {
    return {
      regionId: data.regionId,
      regionCode: data.regionCode,
      regionName: data.regionName,
      zoneId: data.zoneId,
      zoneCode: data.zoneCode,
      zoneName: data.zoneName,
      propertyId: data.propertyId,
      propertyCode: data.propertyCode,
      propertyName: data.propertyName,
      locationId: data.locationId,
      standId: data.standId,
      standName: data.standName,
      standCycle: data.standCycle,
      standRotation: data.standRotation,
      standArea: data.standArea,
      periodId: data.periodId,
      periodName: data.periodName,
      speciesId: data.speciesId,
      species: data.species,
      geneticMaterialTypeId: data.geneticMaterialTypeId,
      geneticMaterialType: data.geneticMaterialType,
      geneticMaterialId: data.geneticMaterialId,
      geneticMaterial: data.geneticMaterial,
      plantationDate: data.plantationDate,
      isOwnPlantation: data.isOwnPlantation,
      age: data.age,
      volumePerHectare: data.volumePerHectare,
      propertyTypeId: data.propertyTypeId,
      propertyTypeName: data.propertyTypeName,
      propertyTypeGroupId: data.propertyTypeGroupId,
      propertyTypeGroupName: data.propertyTypeGroupName,
      rotationUseId: data.rotationUseId,
      rotationUse: data.rotationUse,
      forestrySituationId: data.forestrySituationId,
      forestrySituation: data.forestrySituation,
      forestryStatusId: data.forestryStatusId,
      forestryStatus: data.forestryStatus,
      eucalyptusArea: data.eucalyptusArea,
      departmentId: data.departmentId,
      isPefc: data.isPefc,
      isFsc: data.isFsc,
      landContractId: data.landContractId,
      landContract: data.landContract,
      plantedArea: data.plantedArea,
      isManagedByFO: data.isManagedByFO,
      departmentName: data.departmentName,
      concurrencyStamp: selected.concurrencyStamp,
    };
  }

  protected getEntityName(): string {
    return 'ForestryBase';
  }
}
